import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import { HttpClient } from '@angular/common/http';
import {environment} from '../../../../src/environments/environment';
import {ApiList} from '../../../../src/app/API/api-list.enum';

/**
 * Универсальный REST запрос к серверу
 * служит для получения данных любого типа
 * @service
 */
@Injectable()
export class SemRestRequestService {
  /**
   * Базовый урл состоит из основной и добавленной частей
   * @type {string}
   */
  public baseUrl = environment.backEndUrlPrefix;

  /**
   * @param {HttpClient} http стандартный запрос
   */
  constructor(private http: HttpClient) {
  }

  /**
   * Формирует конечый урл для получения даных
   * ~~~
   * this.semRequestService(ApiList.db, ApiList.service, ApiList.method)
   * ~~~
   * @param {ApiList | number} pathElements элементы из которых строится путь
   * @returns {SemRestRequestService} возвращает сам себя для построения цепочки вызовов
   */
  public baseUrlAppend(...pathElements: (ApiList | number | string)[]): SemRestRequestService {
    const paths = pathElements.map((pathElement) => ApiList[pathElement] ? ApiList[pathElement].toLowerCase() : pathElement);
    this.baseUrl =  [environment.backEndUrlPrefix, ...paths].join('/');
    return this;
  }

  /**
   * Добавляет строковый параметр к baseUrl
   * @param {string} param - параметр
   * @returns {SemRestRequestService}
   */
  public baseUrlAppendByStrParam(param: string): SemRestRequestService {
    this.baseUrl += param;
    return this;
  }

  /**
   * Устанавливает baseUrl
   * @param {string} param - параметр
   * @returns {SemRestRequestService}
   */
  public setBaseUrl(param: string): SemRestRequestService {
    this.baseUrl = param;
    return this;
  }

  /**
   * Добавляет query параметры на основе JSON объекта параметров
   * @param {Object} options -  опции параметра
   * @returns {SemRestRequestService}
   */
  public baseUrlAppendByOptions(options: {}): SemRestRequestService {
    const arrayToStringKeys = (key, value) => {
      let resultStr = `${key}=${JSON.stringify(options[key])}`;
      if (value && value.length) {
        const tmp = value.map(v => `${key}=${JSON.stringify(v)}`);
        resultStr = tmp.join('&');
      }
      return resultStr;
    };
    const hasQueryParam = this.baseUrl.indexOf('?') >= 0;
    const queryParams: string[]  = [...Object.keys(options)]
        .filter(key => options[key] !== undefined)
        .map(key => arrayToStringKeys(key, options[key]));
    return this.baseUrlAppendByStrParam( (hasQueryParam ? '&' : '?') + queryParams.join('&'));
  }

  /**
   * Получает все значения методом GET
   * @returns {Observable<T[]>} обозреватель для async
   */
  public getAll<T>(params?: {}): Observable<T[]> {
    return this.http.get<T[]>(this.baseUrl, {params: params});
  }

  /**
   * Получает целевую страницу как HTML
   * @param params
   */
  public getHTML(params?: {}): Observable<string> {
    return this.http.get(this.baseUrl, {params: params, responseType: 'text'});
  }

  /**
   * Получает единичный объект
   * @returns {Observable<T>}
   */
  public getSingle<T>(): Observable<T> {
    return this.http.get<T>(this.baseUrl);
  }

  /**
   * Позволяет отсылать на сервер post с данными в формате FormData
   * @param formData
   * @returns {Observable<any>}
   */
  public postFormData(formData): Observable<any> {
    return this.http.post(this.baseUrl, formData);
  }

  /**
   * Позволяет отсылать на сервер put с данными в формате FormData
   * @param formData
   * @returns {Observable<any>}
   */
  public putFormData(formData): Observable<any> {
    return this.http.put(this.baseUrl, formData);
  }
  /**
   * Оптравлят набор параметров на сервер post запросом
   * @param data
   * @returns {Observable<T>}
   */
  public postData(data): Observable<Blob> {
    return this.http.post(this.baseUrl, data, {responseType: 'blob'});
  }

  /**
   * Отправлят запрос на удаление, параметры передает как параметры запроса
   * @param data
   * @returns {Observable<Blob>}
   */
  public delete(data): Observable<Object> {
    return this.http.delete(this.baseUrl, {params: data});
  }

  /**
   * Отправляет запрос на удаление, параметр ID отправляется как последняя часть урла
   * @param {number} id
   * @returns {Observable<Blob>}
   */
  public deleteById(id: number): Observable<Object> {
    return this.http.delete([this.baseUrl, id].join('/'));
  }

  /**
   * Отправляет запрос на обновление данных методом PUT
   * @param data
   * @returns {Observable<Object>}
   */
  public update(data) {
    return this.http.put(this.baseUrl, {params: data});
  }

  /**
   * Отправляет запрос на обновление данных методом PUT
   * @param data
   * @returns {Observable<Object>}
   */
  public updateRaw(data) {
    return this.http.put(this.baseUrl, data);
  }

  /**
   * Отправляет запрос на обновление данных методом PUT
   * при этом ID подставляется как последнее значение URL
   * @param id
   * @param data
   * @returns {Observable<Object>}
   */
  public updateById(id, data) {
    return this.http.put([this.baseUrl, id].join('/'), data);
  }

  /**
   * Получает результат запроса в виде blob
   */
  public getBlob(accept?: string): Observable<Blob> {
    return this.http.get(this.baseUrl, {responseType: 'blob', headers: {'Accept': accept ? 'application/octet-stream' : 'text/csv'}});
  }
}
